// src/components/CompanyPreview.js
import React from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { AnimatedSection } from './common/AnimatedSection';

/* ===== viewport hook (768px 기준) ===== */
const useIsMobile = (query = '(max-width: 768px)') => {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return isMobile;
};

/* ===== styled ===== */
const CompanySection = styled.section`
  background: #111111;
  padding: 6rem 0;
  @media (max-width: 768px) { padding: 5rem 0; }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;
  @media (max-width: 768px) { padding: 0 1.5rem; }
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 700;
  color: rgba(0,0,0,0.75);
  text-align: center;
  margin-bottom: 4rem;
  letter-spacing: -1px;
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: calc(100% - 0px);
  height: 75vh;
  min-height: 500px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  // border-radius: 20px;

  @media (max-width: 768px) {
    width: 100%;
    height: 420px; 
    // border-radius: 16px;
    // padding-inline: max(16px, env(safe-area-inset-left))
    //                 max(16px, env(safe-area-inset-right));
  }
  @media (max-width: 480px) { height: 300px; }
`;

const HeroImage = styled.div`
  width: 100%; height: 100%;
  background-image: url('/images/highway-road.jpg');
  background-size: cover; background-position: center; background-repeat: no-repeat;
  position: relative; 
  // border-radius: 20px;

  &::before{
    content:''; position:absolute; inset:0; z-index:1;
    background: rgba(0,0,0,0.4); 
    // border-radius: 20px;
  }
  @media (max-width: 768px){
    // border-radius: 16px; 
    // &::before{ border-radius:16px; }
  }
`;

const TextOverlay = styled.div`
  position: absolute; 
  top: 50%; 
  transform: translateY(-50%);
  z-index: 2; 
  color: #fff; 
  text-align: left;
  left: 4rem; 
  width: 70%;

  @media (max-width: 768px){
    left: auto; inset-inline: 24px; width:auto;
    padding: 12px 4px;
  }
`;

const MainHeadline = styled.h1`
  font-size: clamp(1.4rem, 4.2vw, 2.6rem);
  font-weight: 800; color: #fff; margin-bottom: 1.25rem;
  line-height: 1.25; letter-spacing: -0.02em; white-space: normal;
  @media (max-width: 480px){ letter-spacing: -0.03em; font-size: 1.3rem;}
`;

const SubText = styled.p`
  font-size: clamp(0.95rem, 2.5vw, 1.2rem);
  color:#fff; font-weight: 500; line-height: 1.6;
  letter-spacing: -0.01em; margin-bottom: 2rem;
  @media (max-width: 480px){font-size: 0.8rem;}
`;

const MoreDetailsButton = styled.button`
  background: transparent; border: 1px solid #fff; color:#fff;
  padding: .9rem 1.8rem; font-size: .95rem; font-weight:700;
  text-transform: uppercase; letter-spacing: .08em; cursor: pointer;
  transition: all .28s ease; border-radius: 30px;
  &:hover{ background:#fff; color:#111; transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(255,255,255,.28); }
  @media (max-width:768px){ padding:.8rem 1.4rem; font-size:.8rem; }
`;

/* ===== component ===== */
function CompanyPreview() {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [heroRef, heroVisible] = useScrollAnimation();
  const isMobile = useIsMobile();

  // ↘ 데스크탑/모바일 각각 원하는 줄바꿈(\n)으로 작성
  const subTextDesktop =
    '사람 중심의 기술, 현장 경험, 그리고 미래를 향한 도전으로\n모두가 안심할 수 있는 교통 환경을 실현합니다.';
  const subTextMobile =
    '사람 중심의 기술과 현장 경험으로,\n모두가 안심할 수 있는 교통 환경을 실현합니다.';

  const subtext = isMobile ? subTextMobile : subTextDesktop;

  // \n -> <br/> 변환 렌더러
  const renderMultiline = (text) =>
    String(text || '').split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}{i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

  const handleMoreDetails = () => {
    window.location.href = '/about/ceo';
  };

  return (
    <CompanySection>
      <Container>
        <AnimatedSection ref={titleRef} isVisible={titleVisible} animation="fadeInUp">
          {/* <SectionTitle>ABOUT</SectionTitle> */}
        </AnimatedSection>
      </Container>

      <AnimatedSection ref={heroRef} isVisible={heroVisible} animation="fadeInUp" delay="0.3s">
        <HeroImageContainer>
          <HeroImage>
            <TextOverlay>
              <MainHeadline>대한민국 도로 안전의 미래를 만듭니다</MainHeadline>
              <SubText>{renderMultiline(subtext)}</SubText>
              <MoreDetailsButton onClick={handleMoreDetails}>More Details</MoreDetailsButton>
            </TextOverlay>
          </HeroImage>
        </HeroImageContainer>
      </AnimatedSection>
    </CompanySection>
  );
}

export default CompanyPreview;
